import { GUID } from "@faro-lotv/foundation";
import { createContext, JSX, ReactNode, useMemo, useState } from "react";

/**
 * Context for managing which node in a ScanTree is currently being hovered.
 * This allows nodes to broadcast their hover state and ensures only one preview is visible at a
 * time when the user moves the mouse between entries.
 */
export const ScanTreeNodeHoverContext = createContext<{
  /** The ID of the node currently being hovered, or null if none are hovered. */
  hoveredNodeId: GUID | null;
  /** Setter for the hovered node ID. Pass null to indicate no node is hovered. */
  setHoveredNodeId(id: GUID | null): void;
} | null>(null);

/**
 * @returns Provider component that manages the hover state for a tree of scan nodes.
 * Wrap this around each tree (ScanTree or ActionTree) to enable hover broadcast functionality.
 */
export function ScanTreeNodeHoverProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [hoveredNodeId, setHoveredNodeId] = useState<GUID | null>(null);

  const contextValue = useMemo(
    () => ({ hoveredNodeId, setHoveredNodeId }),
    [hoveredNodeId],
  );

  return (
    <ScanTreeNodeHoverContext.Provider value={contextValue}>
      {children}
    </ScanTreeNodeHoverContext.Provider>
  );
}
